import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useAllAddresses } from 'app/network/address';
import { useMonthlyStatsAll, useMonthlyStreakMembers } from 'app/network/monthly-stats';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { HeroHeader } from './hero-header';
import { MonthlyStreakCard } from './monthly-streak-card';
import { ObjectiveCard } from './objective-card';
import { SavingsCard } from './savings-card';
import { dtoToSavings, dtoToStatus, type HistoryMonth } from './types';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface MonthlyStreakSectionProps {
  navigation: SpecificNavigationProp<'streak'>;
}

export const MonthlyStreakSection = function MonthlyStreakSection({ navigation }: MonthlyStreakSectionProps) {
  const { isLargeUI } = useUISize();
  const parentState = navigation.getParent()?.getState();
  const isInProfile = parentState?.routes[parentState.index]?.name === 'profile';
  useNavigationOptions(
    navigation,
    {
      headerShown: isInProfile && isLargeUI,
    },
    false,
  );

  const { data: statsAll } = useMonthlyStatsAll();
  const currentPeriod = statsAll?.[0]?.period ?? '';
  const { data: membersData } = useMonthlyStreakMembers(currentPeriod, 3, { enabled: !!currentPeriod });
  const { data: addresses } = useAllAddresses();

  const status = statsAll?.[0] ? dtoToStatus(statsAll[0]) : null;
  const history: HistoryMonth[] = statsAll ?? [];
  const savings = statsAll?.[0] ? dtoToSavings(statsAll[0]) : null;
  const postalCode = addresses?.[0]?.postalCode ?? '';
  const members = membersData?.pages[0]?.results ?? [];
  const memberCount = membersData?.pages[0]?.total ?? 0;

  const { t } = useTranslation('streak');

  const insets = useSafeAreaInsets();
  const defaultPadding = usePagePadding();
  const pagePadding = { ...defaultPadding, paddingTop: 0, paddingLeft: 0, paddingRight: 0 };
  const [openObjectiveInfo, setOpenObjectiveInfo] = useState(false);
  const [openSeriesInfo, setOpenSeriesInfo] = useState(false);

  return (
    <Page paddingStyle={pagePadding}>
      <Column gap="spacing.general.x4">
        <Column
          backgroundColor="surface.brand.secondary-3.subtle"
          gap="spacing.general.x4"
          padding="spacing.general.x4"
          style={{ paddingTop: insets.top + theme.spacing['spacing.general.x4'] }}
        >
          <HeroHeader
            memberCount={memberCount}
            members={members}
            onBack={() => navigation.goBack()}
            onMembersPress={() => currentPeriod && navigation.navigate('streak-members', { period: currentPeriod })}
            postalCode={postalCode}
          />

          {status ? <ObjectiveCard status={status} setOpenObjectiveInfo={setOpenObjectiveInfo} /> : null}

          <MonthlyStreakCard history={history} setOpenSeriesInfo={setOpenSeriesInfo} />
        </Column>

        <Column paddingHorizontal="spacing.general.x4">{savings ? <SavingsCard savings={savings} /> : null}</Column>

        <BottomSheet open={openObjectiveInfo} onClose={() => setOpenObjectiveInfo(false)} showIndicator={false}>
          <Column
            gap="spacing.general.x8"
            paddingHorizontal="spacing.general.x4"
            paddingTop="spacing.general.x4"
            paddingBottom="spacing.general.x4"
          >
            <Row alignItems="center" justifyContent="space-between">
              <Pressable
                hitSlop={10}
                onPress={() => setOpenObjectiveInfo(false)}
                style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
              >
                <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} />
              </Pressable>
              <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
                {t('objectiveDetailsTitle')}
              </Text>
            </Row>
            <Text fontSize={16}>{t('objectiveDetails')}</Text>
          </Column>
        </BottomSheet>

        <BottomSheet open={openSeriesInfo} onClose={() => setOpenSeriesInfo(false)} showIndicator={false}>
          <Column
            gap="spacing.general.x8"
            paddingHorizontal="spacing.general.x4"
            paddingTop="spacing.general.x4"
            paddingBottom="spacing.general.x4"
          >
            <Row alignItems="center" justifyContent="space-between">
              <Pressable
                hitSlop={10}
                onPress={() => setOpenSeriesInfo(false)}
                style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
              >
                <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} />
              </Pressable>
              <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
                {t('seriesDetailsTitle')}
              </Text>
            </Row>
            <Text fontSize={16}>{t('seriesDetails')}</Text>
          </Column>
        </BottomSheet>
      </Column>
    </Page>
  );
};
